import React, { useState } from 'react';
import { collection, addDoc, getDocs, deleteDoc, doc, writeBatch } from 'firebase/firestore';
import { db } from '../firebase';
import { useNavigate } from 'react-router-dom';
import { useData } from '../context/DataContext';
import Sidebar, { SIDEBAR_W } from '../components/Sidebar';

function generateCustomerNumber(index) {
  return `N${String(1000 + index).padStart(4, '0')}`;
}

function ImportCustomers({ user }) {
  const navigate = useNavigate();
  const { refreshCustomers } = useData();
  const [status, setStatus] = useState('');
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [importing, setImporting] = useState(false);
  const [done, setDone] = useState(false);
  const [preview, setPreview] = useState([]);
  const [fileReady, setFileReady] = useState(false);
  const [parsedCustomers, setParsedCustomers] = useState([]);
  const [clearExisting, setClearExisting] = useState(false);

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      const text = evt.target.result;
      const lines = text.split('\n');

      const customers = [];
      const seen = new Set();

      // Words that indicate test/junk entries
      const junkWords = [
        'test', 'frankenstein', 'dyrb', 'awawaw', 'eeef', 'awaaaaa3',
        'bbbb', 'mr mustanrd', 'saad khan', 'billy bob', 'johnny',
        'bobs test', 'mr test', 'bob test'
      ];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue;

        const cols = [];
        let current = '';
        let inQuotes = false;
        for (let c = 0; c < line.length; c++) {
          if (line[c] === '"') { inQuotes = !inQuotes; }
          else if (line[c] === ',' && !inQuotes) { cols.push(current.trim()); current = ''; }
          else { current += line[c]; }
        }
        cols.push(current.trim());

        const name = cols[0]?.trim().replace(/"/g, '');
        const phone = cols[1]?.trim() || '';
        const email = cols[2]?.trim() || '';

        if (!name || name.length < 2) continue;

        const lowerName = name.toLowerCase();

        // Skip junk entries
        const isJunk = junkWords.some(w => lowerName.includes(w)) ||
          lowerName === 'c' || lowerName === 'yy' || lowerName === 'apt' ||
          lowerName === 'cal' || lowerName === 'cade' || lowerName === 'micah' ||
          lowerName === 'sarc' || lowerName === 'pmgl' || lowerName === 'sonia c' ||
          lowerName === 'apm' || lowerName === 'jvs' || lowerName === 'elar' ||
          lowerName === 'apт' || lowerName === 'fdc' || lowerName === 'jrs' ||
          lowerName === 'beverly' || lowerName === 'brian' || lowerName === 'cade' ||
          lowerName === 'logan c' || lowerName === 'fallston3' || lowerName === 'mark bel air' ||
          lowerName === 'awawaw' || lowerName === 'dyrb test' || lowerName === 'steve decker';

        if (isJunk) continue;

        // Skip duplicates
        const key = lowerName;
        if (seen.has(key)) continue;
        seen.add(key);

        customers.push({
          name,
          phone,
          email,
          address: '',
          notes: '',
          taxExempt: false,
        });
      }

      // Assign customer numbers sequentially
      const withNumbers = customers.map((c, i) => ({
        ...c,
        customerNumber: generateCustomerNumber(i + 1),
        createdAt: new Date().toISOString(),
      }));

      setParsedCustomers(withNumbers);
      setPreview(withNumbers.slice(0, 10));
      setTotal(withNumbers.length);
      setFileReady(true);
      setStatus(`✅ File read — ${withNumbers.length} customers ready to import.`);
    };

    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (parsedCustomers.length === 0) return;
    setImporting(true);
    setDone(false);
    setProgress(0);

    try {
      // Delete existing if checked
      if (clearExisting) {
        setStatus('Clearing existing customers...');
        const existing = await getDocs(collection(db, 'customers'));
        // Delete in batches of 400
        const batchSize = 400;
        for (let i = 0; i < existing.docs.length; i += batchSize) {
          const batch = writeBatch(db);
          existing.docs.slice(i, i + batchSize).forEach(d => batch.delete(doc(db, 'customers', d.id)));
          await batch.commit();
        }
      }

      // Import in batches of 400
      setStatus('Importing customers...');
      const BATCH_SIZE = 400;
      let count = 0;

      for (let i = 0; i < parsedCustomers.length; i += BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = parsedCustomers.slice(i, i + BATCH_SIZE);

        chunk.forEach(customer => {
          const ref = doc(collection(db, 'customers'));
          batch.set(ref, customer);
        });

        await batch.commit();
        count += chunk.length;
        setProgress(count);
        setStatus(`Importing... ${count} of ${parsedCustomers.length}`);

        // Small pause between batches so browser doesn't freeze
        await new Promise(resolve => setTimeout(resolve, 200));
      }

      setStatus(`✅ Done! ${count} customers imported successfully.`);
      setDone(true);
      refreshCustomers();
    } catch (err) {
      console.error('Import error:', err);
      setStatus(`❌ Error: ${err.message}`);
    }

    setImporting(false);
  };

  return (
    <>
      <Sidebar />
      <div className="min-h-screen" style={{ marginLeft: SIDEBAR_W, backgroundColor: '#CDE7F5', fontFamily: 'Manrope, sans-serif' }}>

      <div className="max-w-4xl mx-auto p-6">

        {/* Slim header row */}
        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <h1 className="text-xl font-bold" style={{ color: '#03045E' }}>👥 Import Customers from CSV</h1>
          <button
            onClick={() => navigate('/customers')}
            className="text-sm font-semibold px-4 py-2 rounded-lg border transition"
            style={{ backgroundColor: '#E6F1FB', color: '#0077B6', borderColor: '#E3ECF5' }}
          >
            ← Customers
          </button>
        </div>

        <div className="rounded-xl p-6 mb-6" style={{ backgroundColor: '#FFFFFF', border: '1px solid #E3ECF5' }}>
          <p className="text-sm mb-6" style={{ color: '#6B7C93' }}>
            Upload your CSV file. Test entries and duplicates are filtered automatically.
            Customers are imported in batches to avoid browser freezing.
          </p>

          {/* Clear existing option */}
          <div className="rounded-lg p-4 mb-6" style={{ backgroundColor: '#FDF1DD', border: '1px solid #854F0B' }}>
            <div className="flex items-center gap-3">
              <input type="checkbox" id="clearExisting" checked={clearExisting}
                onChange={(e) => setClearExisting(e.target.checked)} className="w-4 h-4" style={{ accentColor: '#854F0B' }} />
              <label htmlFor="clearExisting" className="font-semibold" style={{ color: '#854F0B' }}>
                Delete existing customers before importing
              </label>
            </div>
            <p className="text-xs mt-1 ml-7" style={{ color: '#854F0B' }}>
              Check this to replace all customers. Leave unchecked to add to existing ones.
            </p>
          </div>

          {/* File Upload */}
          <div className="rounded-xl p-8 text-center mb-6" style={{ border: '2px dashed #0077B6', backgroundColor: '#E6F1FB' }}>
            <p className="mb-4" style={{ color: '#6B7C93' }}>Select your CSV file</p>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              className="block mx-auto text-sm file:mr-4 file:py-2 file:px-6 file:rounded-lg file:border-0 file:font-bold file:bg-[#0077B6] file:text-white hover:file:bg-[#03045E]"
              style={{ color: '#6B7C93' }}
            />
          </div>

          {/* Status */}
          {status && (
            <div
              className="rounded-lg px-4 py-3 mb-6 font-semibold"
              style={
                status.includes('✅') ? { backgroundColor: '#E6F7EE', color: '#0F6E56' } :
                status.includes('❌') ? { backgroundColor: '#FBECEC', color: '#A32D2D' } :
                { backgroundColor: '#E6F1FB', color: '#0077B6' }
              }
            >
              {status}
            </div>
          )}

          {/* Progress Bar */}
          {importing && (
            <div className="mb-6">
              <div className="flex justify-between text-sm mb-1" style={{ color: '#6B7C93' }}>
                <span>Importing in batches...</span>
                <span>{progress} / {total}</span>
              </div>
              <div className="w-full rounded-full h-4" style={{ backgroundColor: '#E6F1FB' }}>
                <div className="h-4 rounded-full transition-all"
                  style={{ width: `${total > 0 ? (progress / total) * 100 : 0}%`, backgroundColor: '#0077B6' }} />
              </div>
              <p className="text-xs mt-2 text-center" style={{ color: '#6B7C93' }}>Please wait — do not close the browser</p>
            </div>
          )}

          {/* Preview */}
          {preview.length > 0 && (
            <div className="mb-6">
              <h3 className="font-bold mb-3" style={{ color: '#03045E' }}>Preview (first 10 customers):</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead style={{ backgroundColor: '#E6F1FB' }}>
                    <tr style={{ color: '#03045E' }}>
                      <th className="px-3 py-2 text-left">Customer #</th>
                      <th className="px-3 py-2 text-left">Name</th>
                      <th className="px-3 py-2 text-left">Phone</th>
                      <th className="px-3 py-2 text-left">Email</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((c, i) => (
                      <tr key={i} style={{ backgroundColor: i % 2 === 0 ? '#FFFFFF' : '#CDE7F5' }}>
                        <td className="px-3 py-1.5 font-mono font-bold" style={{ color: '#0077B6' }}>{c.customerNumber}</td>
                        <td className="px-3 py-1.5 font-semibold" style={{ color: '#03045E' }}>{c.name}</td>
                        <td className="px-3 py-1.5" style={{ color: '#6B7C93' }}>{c.phone}</td>
                        <td className="px-3 py-1.5" style={{ color: '#6B7C93' }}>{c.email}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Button */}
          {fileReady && !done && (
            <button onClick={handleImport} disabled={importing}
              className="font-bold px-8 py-3 rounded-xl text-lg transition disabled:opacity-50 w-full"
              style={{ backgroundColor: '#FDF1DD', color: '#854F0B', border: '1px solid #854F0B' }}>
              {importing ? `Importing ${progress}/${total}...` : `🚀 Import ${total} Customers`}
            </button>
          )}

          {done && (
            <button onClick={() => navigate('/customers')}
              className="font-bold px-8 py-3 rounded-xl text-lg transition w-full"
              style={{ backgroundColor: '#0077B6', color: '#FFFFFF' }}>
              ✅ Go to Customer Database
            </button>
          )}
        </div>
      </div>
      </div>
    </>
  );
}

export default ImportCustomers;